import Checkbox from "@material-ui/core/Checkbox";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import React, { CSSProperties, useState } from "react";

import classes from "./BaseDataTable.module.scss";

export type ColumnType = {
  key: string;
  label: string;
  minWidth?: number | string;
  align?: "right" | "left" | "center";
  format?: (value: number) => string;
  customStyles?: CSSProperties;
};

export type CollapsableRowConfigsType = {
  collapsable: boolean;
  collapsableChild: JSX.Element;
  expandControlHandler?: () => void;
};

export type RowType<T extends readonly { key: string }[]> = Record<
  T[number]["key"],
  string | number | React.ReactNode
> & {
  id: string;
  collapsableConfigs?: CollapsableRowConfigsType;
};

type BaseDataTableProps<T extends readonly ColumnType[]> = {
  columns: T;
  rows: RowType<T>[];
  showCheckbox?: boolean;
  selectedItems?: string[];
  toggleCheckbox?: (id: string) => void;
};

const BaseDataTable = <T extends readonly ColumnType[]>({
  columns,
  rows,
  showCheckbox = false,
  selectedItems = [],
  toggleCheckbox,
}: BaseDataTableProps<T>) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [expandedRowIndexes, setExpandedRowIndexes] = useState<number[]>([]);

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleExpandControlCellClick = (clickedRow: RowType<readonly ColumnType[]>, clickedRowIndex: number) => () => {
    setExpandedRowIndexes((prevExpandedRowIndexes) =>
      prevExpandedRowIndexes.includes(clickedRowIndex)
        ? prevExpandedRowIndexes.filter((index) => index !== clickedRowIndex)
        : [...prevExpandedRowIndexes, clickedRowIndex],
    );

    if (clickedRow?.collapsableConfigs?.expandControlHandler) {
      clickedRow?.collapsableConfigs?.expandControlHandler();
    }
  };

  const hasCollapsableRow = rows.some((row) => row.collapsableConfigs?.collapsable);

  return (
    <Paper className={classes.root}>
      <div className={classes.container}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {showCheckbox && (
                  <TableCell
                    style={{
                      width: 40,
                    }}
                  />
                )}

                {hasCollapsableRow ? <TableCell>{null}</TableCell> : null}
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                    className={classes.columnLabel}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: RowType<readonly ColumnType[]>, rowIndex: number) => (
                  <React.Fragment key={row.id}>
                    <TableRow hover role="checkbox" tabIndex={-1}>
                      {showCheckbox && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedItems.includes(String(row.id))}
                            onChange={() => {
                              if (toggleCheckbox) {
                                toggleCheckbox(row.id);
                              }
                            }}
                          />
                        </TableCell>
                      )}

                      {row.collapsableConfigs?.collapsable ? (
                        <TableCell
                          onClick={handleExpandControlCellClick(row, rowIndex)}
                          className={classes.expandControlCell}
                        >
                          {expandedRowIndexes.includes(rowIndex) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </TableCell>
                      ) : null}

                      {columns.map((column) => {
                        const value = row[column.key];
                        return (
                          <TableCell
                            key={column.key}
                            align={column.align}
                            style={{
                              whiteSpace: "pre-line",
                              ...column.customStyles,
                            }}
                          >
                            {column.format && typeof value === "number" ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {row.collapsableConfigs?.collapsable ? (
                      <TableRow>
                        <TableCell className={classes.collapsableCell} colSpan={6}>
                          <Collapse in={expandedRowIndexes.includes(rowIndex)}>
                            {row.collapsableConfigs.collapsableChild}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > 10 ? (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        ) : null}
      </div>
    </Paper>
  );
};

export default BaseDataTable;
